import React from 'react';

import { Stack, Tooltip } from '@mui/material';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { sendVerificationCodeViaPhone } from '../../../../api/security';
import {
  ButtonStyled,
  ButtonTextStyled,
  FormTextLabelStyled,
  TextFieldStyled,
  TooltipSx,
  TypographyInfoStyled,
} from '../../styles';
import { IPhoneForm } from '../../types';

interface IProps {
  onComplete: (phone: string) => void;
  onEmail: () => void;
}

const PhoneForm = React.memo(({ onComplete, onEmail }: IProps) => {
  const [isProcess, setProcess] = React.useState<boolean>(false);
  const [phoneNotFound, setPhoneNotFound] = React.useState<boolean>(false);

  const { t } = useTranslation();

  const handlePhoneKeyUp = React.useCallback(() => {
    if (!phoneNotFound) {
      return;
    }

    setPhoneNotFound(false);
  }, [phoneNotFound]);

  const onSubmit = React.useCallback(
    async (values: IPhoneForm) => {
      setProcess(true);

      try {
        const { status } = await sendVerificationCodeViaPhone(values);

        if (status) {
          onComplete(values.phone);
        } else {
          setPhoneNotFound(true);
        }
      } catch {
        setPhoneNotFound(true);
      } finally {
        setProcess(false);
      }
    },
    [onComplete, setPhoneNotFound],
  );

  return (
    <Formik
      initialValues={{
        phone: '',
      }}
      validationSchema={Yup.object({
        phone: Yup.string()
          .matches(/^\+[\d]{9,15}$/, t('signin.phone.form_error'))
          .required(t('signin.phone.phone_empty')),
      })}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Form>
          <Stack spacing={3} alignItems="center">
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <Stack spacing={1}>
                <FormTextLabelStyled htmlFor="phone">{t('general.phone')}</FormTextLabelStyled>

                <Tooltip
                  title={phoneNotFound ? t('signin.phone.not_found') : formik.errors.phone}
                  open={phoneNotFound || !!formik.errors.phone}
                  arrow
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: TooltipSx,
                    },
                  }}
                >
                  <TextFieldStyled
                    variant="outlined"
                    autoComplete="off"
                    {...formik.getFieldProps('phone')}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    id="phone"
                    onKeyUp={phoneNotFound ? handlePhoneKeyUp : undefined}
                  />
                </Tooltip>
              </Stack>

              <ButtonStyled
                variant="contained"
                color="primary"
                disabled={!formik.dirty || !formik.isValid || isProcess}
                onClick={() => formik.handleSubmit()}
              >
                {t('signin.phone.send')}
              </ButtonStyled>
            </Stack>

            <Stack width="100%" alignItems="center">
              {!phoneNotFound && (
                <TypographyInfoStyled>{t('signin.phone.info')}</TypographyInfoStyled>
              )}

              {phoneNotFound && (
                <ButtonTextStyled variant="text" onClick={onEmail}>
                  {t('signin.phone.login_via_email')}
                </ButtonTextStyled>
              )}
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
});

export default PhoneForm;
